import { z } from 'zod'
import {
  AREAS,
  IDENTITY_PROVIDERS,
  MEMBERSHIP_ROLES,
  ORG_KINDS,
  PLATFORM_ROLES,
  USER_STATUSES,
} from '../roles'
import { revisionSchema, text } from '../validators'

/**
 * Sign-in, session and account contracts (A-01..A-10, ARCHITECTURE §6).
 * DTOs are snake_case; request schemas are reused by the web forms (P-UI-008).
 */

/** A lower-cased, trimmed email address (RFC 5321 length limit). */
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .pipe(z.email({ message: 'Enter a valid email address.' }))

/** An invitation token as it appears in `/invite/:token` (base64url, 256-bit). */
export const inviteTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{20,128}$/)

/**
 * A post-sign-in destination: a same-origin relative path only. Rejects
 * `//host`, `/\host`, absolute URLs, control characters and the API itself.
 */
export function isSafeNextPath(value: string | null | undefined): value is string {
  if (!value || value.length > 512) return false
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return false
  if (value.startsWith('/api/') || value === '/api') return false
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i)
    if (code < 0x20 || code === 0x7f || value[i] === '\\') return false
  }
  return true
}

export const OAUTH_PROVIDERS = ['google', 'microsoft'] as const
export type OAuthProvider = (typeof OAUTH_PROVIDERS)[number]

/** `GET /auth/providers`: which sign-in options are configured (P-ID-015). */
export const authProvidersSchema = z.object({
  google: z.boolean(),
  microsoft: z.boolean(),
  email: z.boolean(),
})
export type AuthProviders = z.infer<typeof authProvidersSchema>

/** `POST /auth/otp/request` (A-04). Always answers 202, whether or not the email has access. */
export const otpRequestSchema = z.strictObject({
  email: emailSchema,
  invite: inviteTokenSchema.optional(),
})
export type OtpRequest = z.infer<typeof otpRequestSchema>

export const OTP_CODE_PATTERN = /^\d{6}$/
export const RECOVERY_CODE_PATTERN = /^[A-Z2-9]{5}-[A-Z2-9]{5}$/

/**
 * How the code for this email is obtained (ADR-D08): `email` sends a one-time
 * code; `authenticator` sends nothing, because the user has an authenticator
 * app enrolled and signs in with its code (or a recovery code) instead.
 */
export const SIGN_IN_CODE_METHODS = ['email', 'authenticator'] as const
export type SignInCodeMethod = (typeof SIGN_IN_CODE_METHODS)[number]
export const otpRequestResultSchema = z.object({
  ok: z.literal(true),
  method: z.enum(SIGN_IN_CODE_METHODS),
})
export type OtpRequestResult = z.infer<typeof otpRequestResultSchema>

/** `POST /auth/otp/verify`: the emailed code, an authenticator code or a recovery code. */
export const otpVerifySchema = z.strictObject({
  email: emailSchema,
  code: z
    .string()
    .trim()
    .max(32)
    .transform((v) => v.toUpperCase().replace(/\s+/g, ''))
    .refine((v) => OTP_CODE_PATTERN.test(v) || RECOVERY_CODE_PATTERN.test(v), {
      message: 'Enter the 6-digit code.',
    }),
  next: z.string().max(512).optional(),
})
export type OtpVerify = z.infer<typeof otpVerifySchema>

/** Successful sign-in (OTP verify); OAuth redirects instead. */
export const signInResultSchema = z.object({ ok: z.literal(true), next: z.string() })
export type SignInResult = z.infer<typeof signInResultSchema>

export const okSchema = z.object({ ok: z.literal(true) })

// ---------------------------------------------------------------------------
// GET /me
// ---------------------------------------------------------------------------

export const meMembershipSchema = z.object({
  org_id: z.string(),
  org_name: z.string(),
  org_slug: z.string(),
  org_kind: z.enum(ORG_KINDS),
  role: z.enum(MEMBERSHIP_ROLES),
})
export type MeMembership = z.infer<typeof meMembershipSchema>

export const impersonatorSchema = z.object({
  id: z.string(),
  email: z.string(),
  display_name: z.string(),
})
export type Impersonator = z.infer<typeof impersonatorSchema>

export const mfaStatusSchema = z.object({
  /** TOTP is enrolled for this user. */
  enrolled: z.boolean(),
  enrolled_at: z.string().nullable(),
  /** When this session last passed a second-factor check. */
  verified_at: z.string().nullable(),
  /** `PRIVILEGED_MFA=required` and the user is staff or a platform user (P-ID-012). */
  required: z.boolean(),
  recovery_codes_remaining: z.number().int(),
})
export type MfaStatus = z.infer<typeof mfaStatusSchema>

export const meSchema = z.object({
  user: z.object({
    id: z.string(),
    email: z.string(),
    display_name: z.string(),
    status: z.enum(USER_STATUSES),
    revision: z.number().int(),
  }),
  platform_role: z.enum(PLATFORM_ROLES).nullable(),
  memberships: z.array(meMembershipSchema),
  areas: z.array(z.enum(AREAS)),
  /** Set while an operator is viewing as this user (P-ADM-012). */
  impersonated_by: impersonatorSchema.nullable(),
  /** When an impersonated session ends (60 minutes after it started). */
  session_expires_at: z.string(),
  mfa: mfaStatusSchema,
})
export type Me = z.infer<typeof meSchema>

/** `PATCH /me`: the user's own display name. */
export const profileUpdateSchema = z.strictObject({
  revision: revisionSchema,
  display_name: text(2, 120),
})
export type ProfileUpdate = z.infer<typeof profileUpdateSchema>

export const SESSION_METHODS = [...IDENTITY_PROVIDERS, 'impersonation'] as const

/** `GET /me/sessions` item. */
export const sessionInfoSchema = z.object({
  id: z.string(),
  method: z.enum(SESSION_METHODS),
  created_at: z.string(),
  last_active_at: z.string(),
  expires_at: z.string(),
  current: z.boolean(),
  user_agent: z.string().nullable(),
  impersonated: z.boolean(),
})
export type SessionInfo = z.infer<typeof sessionInfoSchema>

export const sessionListSchema = z.object({ items: z.array(sessionInfoSchema) })
export type SessionList = z.infer<typeof sessionListSchema>

// ---------------------------------------------------------------------------
// MFA (A-07)
// ---------------------------------------------------------------------------

export const totpEnrolStartSchema = z.object({
  /** Base32 secret for manual entry. Shown once, never returned again. */
  secret: z.string(),
  otpauth_uri: z.string(),
})
export type TotpEnrolStart = z.infer<typeof totpEnrolStartSchema>

export const totpCodeSchema = z.strictObject({
  code: z.string().trim().regex(OTP_CODE_PATTERN, { message: 'Enter the 6-digit code.' }),
})
export type TotpCode = z.infer<typeof totpCodeSchema>

export const RECOVERY_CODE_COUNT = 10

export const recoveryCodesSchema = z.object({ recovery_codes: z.array(z.string()) })
export type RecoveryCodes = z.infer<typeof recoveryCodesSchema>

/** Step-up: a TOTP code or a one-time recovery code (case and spacing are forgiven). */
export const mfaVerifySchema = z.strictObject({
  code: z
    .string()
    .trim()
    .max(32)
    .transform((v) => v.toUpperCase().replace(/\s+/g, ''))
    .refine((v) => OTP_CODE_PATTERN.test(v) || RECOVERY_CODE_PATTERN.test(v), {
      message: 'Enter the 6-digit code from your app or a recovery code.',
    }),
})
export type MfaVerify = z.infer<typeof mfaVerifySchema>

export const mfaVerifiedSchema = z.object({
  ok: z.literal(true),
  mfa_verified_at: z.string(),
  recovery_code_used: z.boolean(),
})
export type MfaVerified = z.infer<typeof mfaVerifiedSchema>

// ---------------------------------------------------------------------------
// Invitations (public lookup, P-ID-010)
// ---------------------------------------------------------------------------

export const INVITE_STATUSES = ['pending', 'accepted', 'revoked', 'expired'] as const
export type InviteStatus = (typeof INVITE_STATUSES)[number]

export const inviteLookupSchema = z.object({
  status: z.enum(INVITE_STATUSES),
  org_name: z.string(),
  org_kind: z.enum(ORG_KINDS),
  role: z.enum(MEMBERSHIP_ROLES),
  /** Masked, e.g. `a•••@nordlys.example`. */
  email_hint: z.string(),
  expires_at: z.string(),
})
export type InviteLookup = z.infer<typeof inviteLookupSchema>

/** Mask an email for display to someone holding only the link. */
export function maskEmail(email: string): string {
  const at = email.lastIndexOf('@')
  if (at <= 0) return '•••'
  return `${email[0]}•••${email.slice(at)}`
}
